import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { PlusCircle, Trash2, Box, XCircle, Tag } from 'lucide-react';
import Button from './Button';
import Card from './Card';
import Input from './Input';
import SearchableSelectBox from './SearchableSelectBox';

const getNewRow = (id) => ({
    id: id,
    itemId: '',
    quantity: 1,
    targetPrice: 0.00,
    taxId: '',
    comments: '',
    amount: 0.00,
});

const RFQItemRow = ({
    row,
    onChange,
    onDelete,
    allItemOptions,
    allTaxOptions,
    currencySymbol = '₦'
}) => {
    const findOption = useCallback((id, options) => {
        return options.find(opt => opt.value === id);
    }, []);

    const selectedItem = findOption(row.itemId, allItemOptions);

    // Auto-fill unit rate from catalog price if blank
    useEffect(() => {
        if (!selectedItem) return;

        let price = parseFloat(row.targetPrice || 0);
        const qty = parseFloat(row.quantity || 1);

        if (selectedItem.price && price === 0) {
            onChange(row.id, 'targetPrice', selectedItem.price.toFixed(2));
            price = selectedItem.price;
        }

        const subtotal = price * qty;
        const tax = findOption(row.taxId, allTaxOptions);
        const taxRate = (tax?.rate || 0) / 100;
        const taxAmount = subtotal * taxRate;
        const total = subtotal + taxAmount;

        if (Math.abs(total - parseFloat(row.amount || 0)) > 0.01) {
            onChange(row.id, 'amount', total.toFixed(2));
        }
    }, [row.itemId, row.quantity, row.targetPrice, row.taxId, selectedItem, allTaxOptions, onChange, row.id, row.amount, findOption]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        onChange(row.id, name, value);
    };

    // Mobile View
    const MobileRowContent = (
        <div className="md:hidden space-y-3 p-4 border-b border-gray-100 last:border-b-0">
            <div className="flex flex-col gap-1 text-sm">
                <label className="block text-xs font-medium text-gray-500 flex items-center gap-1">
                    <Tag size={14} className="text-indigo-600" /> Item Details
                </label>
                <SearchableSelectBox
                    name="itemId"
                    value={row.itemId}
                    onChange={handleChange}
                    options={allItemOptions}
                    placeholder="Select Item..."
                    containerClassName="!mb-0"
                    className="!text-xs"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                />
            </div>

            <div className="flex gap-4">
                <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Qty</label>
                    <Input
                        type="number"
                        name="quantity"
                        value={row.quantity}
                        onChange={handleChange}
                        min="0.01"
                        step="any"
                        placeholder="1"
                        containerClassName="!mb-0"
                        className="!text-sm !py-1 !px-2 text-center"
                    />
                </div>
                <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Target Rate</label>
                    <Input
                        type="number"
                        name="targetPrice"
                        value={row.targetPrice}
                        onChange={handleChange}
                        min="0"
                        placeholder="0.00"
                        containerClassName="!mb-0"
                        className="!text-sm !py-1 !px-2"
                    />
                </div>
            </div>

            <div className="flex gap-4">
                <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Tax</label>
                    <SearchableSelectBox
                        name="taxId"
                        value={row.taxId}
                        onChange={handleChange}
                        options={allTaxOptions}
                        containerClassName="!mb-0"
                        className="!text-xs"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                    />
                </div>
                <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Remarks</label>
                    <Input
                        type="text"
                        name="comments"
                        value={row.comments}
                        onChange={handleChange}
                        placeholder="Specs / Notes"
                        containerClassName="!mb-0"
                        className="!text-sm !py-1 !px-2"
                    />
                </div>
            </div>

            <div className="flex justify-between items-center pt-2 border-t border-dashed border-gray-100 mt-3">
                <div className="text-lg font-bold text-gray-900">
                    Amount: {currencySymbol}{parseFloat(row.amount || 0).toFixed(2)}
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    className="p-1 text-red-600 hover:bg-red-50"
                    onClick={() => onDelete(row.id)}
                >
                    <Trash2 size={20} />
                </Button>
            </div>
        </div>
    );

    // Desktop View
    const DesktopRowContent = (
        <tr className="hidden md:table-row border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors">
            <td className="px-3 py-2">
                <SearchableSelectBox
                    name="itemId"
                    value={row.itemId}
                    onChange={handleChange}
                    options={allItemOptions}
                    placeholder="Select Item..."
                    containerClassName="!mb-0"
                    className="!text-sm"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                />
            </td>
            <td className="w-24 px-3 py-2">
                <Input
                    type="number"
                    name="quantity"
                    value={row.quantity}
                    onChange={handleChange}
                    min="0.01"
                    step="any"
                    placeholder="1"
                    containerClassName="!mb-0"
                    className="!text-sm !py-1 !px-2 text-center"
                />
            </td>
            <td className="w-28 px-3 py-2">
                <Input
                    type="number"
                    name="targetPrice"
                    value={row.targetPrice}
                    onChange={handleChange}
                    min="0"
                    placeholder="0.00"
                    containerClassName="!mb-0"
                    className="!text-sm !py-1 !px-2"
                />
            </td>
            <td className="w-32 px-3 py-2">
                <SearchableSelectBox
                    name="taxId"
                    value={row.taxId}
                    onChange={handleChange}
                    options={allTaxOptions}
                    containerClassName="!mb-0"
                    className="!text-sm"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                />
            </td>
            <td className="px-3 py-2">
                <Input
                    type="text"
                    name="comments"
                    value={row.comments}
                    onChange={handleChange}
                    placeholder="Item details / specifications"
                    containerClassName="!mb-0"
                    className="!text-sm !py-1 !px-2"
                />
            </td>
            <td className="w-28 px-3 py-2 text-right">
                <span className="font-bold text-gray-900">
                    {currencySymbol}{parseFloat(row.amount || 0).toFixed(2)}
                </span>
            </td>
            <td className="w-10 px-1 py-2 text-center">
                <Button
                    variant="ghost"
                    size="sm"
                    className="p-1 text-red-600 hover:bg-red-50"
                    onClick={() => onDelete(row.id)}
                >
                    <Trash2 size={16} />
                </Button>
            </td>
        </tr>
    );

    return (
        <>
            {DesktopRowContent}
            {MobileRowContent}
        </>
    );
};

const RFQItemsTable = ({
    itemOptions = [],
    taxOptions = [],
    currencySymbol = '₦',
    onItemsChange,
    overrideItems = null
}) => {
    const [items, setItems] = useState([]);
    const [nextId, setNextId] = useState(1);

    const handleAddRow = () => {
        setItems(prev => [...prev, getNewRow(nextId)]);
        setNextId(prev => prev + 1);
    };

    const handleDeleteRow = useCallback((id) => {
        setItems(prev => prev.filter(row => row.id !== id));
    }, []);

    const handleRowChange = useCallback((id, name, value) => {
        setItems(prev => prev.map(row => {
            if (row.id === id) {
                return { ...row, [name]: value };
            }
            return row;
        }));
    }, []);

    useEffect(() => {
        if (onItemsChange) {
            onItemsChange(items);
        }
    }, [items, onItemsChange]);

    useEffect(() => {
        if (overrideItems && Array.isArray(overrideItems) && overrideItems.length > 0) {
            setItems(overrideItems);
            const maxId = overrideItems.reduce((max, item) => (item.id > max ? item.id : max), 0);
            setNextId(maxId + 1);
        }
    }, [overrideItems]);

    const totals = useMemo(() => {
        const grandTotal = items.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
        return { grandTotal };
    }, [items]);

    return (
        <Card className="p-0 overflow-hidden">
            <div className="p-4 bg-gray-100 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <Box size={20} className="text-indigo-600" /> Quoted Items
                </h2>
                <Button
                    variant="secondary"
                    onClick={handleAddRow}
                    size="sm"
                    className="flex items-center gap-1"
                >
                    <PlusCircle size={16} /> Add Quoted Item
                </Button>
            </div>

            <div className="overflow-x-auto hidden md:block">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="text-left font-medium text-gray-700 bg-gray-50 border-b">
                            <th className="px-3 py-2 w-auto">Item Details</th>
                            <th className="px-3 py-2 w-24 text-center">Qty</th>
                            <th className="px-3 py-2 w-28 text-center">Target Price</th>
                            <th className="px-3 py-2 w-32 text-center">Tax Category</th>
                            <th className="px-3 py-2 w-auto">Item Specifications</th>
                            <th className="px-3 py-2 w-28 text-right">Estimated Total</th>
                            <th className="px-3 py-2 w-10"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(row => (
                            <RFQItemRow
                                key={row.id}
                                row={row}
                                onChange={handleRowChange}
                                onDelete={handleDeleteRow}
                                allItemOptions={itemOptions}
                                allTaxOptions={taxOptions}
                                currencySymbol={currencySymbol}
                            />
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="md:hidden divide-y divide-gray-100">
                {items.length === 0 && (
                    <div className="p-4 text-center text-gray-500">
                        <XCircle size={24} className="mx-auto mb-2 text-gray-400" />
                        <p>No items added yet. Click 'Add Quoted Item' to start.</p>
                    </div>
                )}
                {items.map(row => (
                    <RFQItemRow
                        key={row.id}
                        row={row}
                        onChange={handleRowChange}
                        onDelete={handleDeleteRow}
                        allItemOptions={itemOptions}
                        allTaxOptions={taxOptions}
                        currencySymbol={currencySymbol}
                    />
                ))}
            </div>

            <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                <div className="w-full md:w-1/3 space-y-2 text-sm">
                    <div className="flex justify-between text-lg font-bold text-indigo-600 border-t pt-2 mt-2">
                        <span>Total Estimated Value:</span>
                        <span>{currencySymbol}{totals.grandTotal.toFixed(2)}</span>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default RFQItemsTable;